import {
  canAccessReports,
  DataPermission,
  isPrivileged,
  UserRole,
} from '../core/user-context/user-context.model';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
}

export interface NavGroup {
  /** Section heading; omitted for a heading-less group (the restricted nav). */
  label?: string;
  items: NavItem[];
}

/**
 * Builds the side-nav groups visible to the current user (FDD §2.4.1, §4). Each
 * module is its own section (Employee Monitoring, Prehire, Reports, Admin Settings)
 * per the Figma side panel. Monitoring sections follow assigned data types;
 * Reports shows for POC / NABP and Authorized Users with view/edit access (not
 * view-only). POC, NABP Admin and NABP Staff all get the same nav, including the full
 * Admin Settings section. Its Location Management item only shows when the active org
 * has Location Management enabled (Figma 1619-411269). A restricted Authorized User
 * (no data type, or no location when Location Management is on) gets only their own
 * Session Audit Log, with no section heading (Figma 3667-695573) — plus Reports for
 * a view/edit user, whose PSV tab then explains why it can't run. Icons are
 * Material Symbols ligatures.
 */
export function buildNavGroups(
  role: UserRole | null,
  dataTypes: DataPermission[],
  locationManagementEnabled = false,
  accessRestricted = false,
): NavGroup[] {
  const privileged = isPrivileged(role);
  const canEmployee = privileged || dataTypes.includes(DataPermission.EmployeeData);
  const canPreHire = privileged || dataTypes.includes(DataPermission.PreHireData);

  const sessionAuditLog: NavItem = {
    label: 'Session Audit Log',
    icon: 'summarize',
    route: '/admin/audit-log',
  };

  const reports: NavGroup = {
    label: 'Reports',
    items: [{ label: 'Reports', icon: 'description', route: '/reports' }],
  };

  if (accessRestricted) {
    return [...(canAccessReports(role) ? [reports] : []), { items: [sessionAuditLog] }];
  }

  const groups: NavGroup[] = [];

  if (canEmployee) {
    groups.push({
      label: 'Employee Monitoring',
      items: [{ label: 'All Employees', icon: 'people', route: '/employee' }],
    });
  }

  if (canPreHire) {
    groups.push({
      label: 'Prehire',
      items: [{ label: 'Prehire Review', icon: 'badge', route: '/pre-hire' }],
    });
  }

  if (canAccessReports(role)) {
    groups.push(reports);
  }

  if (privileged) {
    groups.push({
      label: 'Admin Settings',
      items: [
        { label: 'User Management', icon: 'manage_accounts', route: '/admin/users' },
        ...(locationManagementEnabled
          ? [{ label: 'Location Management', icon: 'place', route: '/admin/locations' }]
          : []),
        { label: 'Organization Settings', icon: 'settings', route: '/admin/settings' },
        sessionAuditLog,
      ],
    });
  } else if (role) {
    // Every other user sees their own sessions (FDD §7.2, Figma 688-249133) — the
    // Session Audit Log stays under Admin Settings, without the admin-only pages.
    groups.push({ label: 'Admin Settings', items: [sessionAuditLog] });
  }

  return groups;
}
